import firestore from "src/apiService/firestore";
import { userPlanSelector } from "src/store/system/selector";
import { getUploads } from "src/store/uploads/selector";
import {
  getExtensionId,
  checkExtensionVersion,
  MissingExtensionError,
  OutdatedExtensionError,
} from "src/utils/extension";
import type {
  PoshmarkIntegration,
  MercariIntegration,
  WhatnotIntegration,
  VintedIntegration,
  DepopIntegration,
} from "src/interfaces/plaidIntegration.interface";

import type { IsRunningKey, Dispatcher, SyncStatus } from "../types";
import { SyncLockTtlMs } from "./syncLock";

/** UI “is running” window — must match mutex TTL or Import stays clickable while withSyncLock blocks. */
export const IsRunningThreshold = SyncLockTtlMs;
export const ReSyncInterval = 24 * 60 * 60 * 1000; // 24hs
export const RunningKeys: IsRunningKey[] = [
  "inventoryIsRunning",
  "salesIsRunning",
];

export function syncAlreadyLockedMessage(platform: IntegrationPlatformName) {
  return `${platform} sync is already in progress or temporarily locked by another tab. Please wait a few minutes while the system automatically resumes.`;
}

/**
 * Console + in-app snackbar when `withSyncLock` cannot acquire the mutex.
 * Leaves `type: "locked"` status set so the caller must not clear it immediately.
 */
export function notifySyncAlreadyLocked(
  dispatch: Function,
  updateSyncStatus: (status: SyncStatus | undefined) => any,
  platform: IntegrationPlatformName
) {
  const message = syncAlreadyLockedMessage(platform);
  console.warn(`[${platform}] ${message}`);
  dispatch(
    updateSyncStatus({
      type: "locked",
      message,
    })
  );
}

export function checkIfItIsInitialImport(
  state: any,
  key:
    | "poshmarkSales"
    | "poshmarkInventory"
    | "mercariSales"
    | "mercariInventory"
    | "whatnotSales"
    | "vintedSales"
    | "vintedInventory"
    | "depopSales"
    | "depopInventory"
) {
  const uploads = getUploads(state);

  for (const upload of uploads) {
    if (upload.filename?.includes(key)) return false;
  }

  return true;
}

export function isUserAllowedToDirectImport(
  state: any,
  key:
    | "poshmarkSales"
    | "poshmarkInventory"
    | "mercariSales"
    | "mercariInventory"
    | "whatnotSales"
    | "vintedSales"
    | "vintedInventory"
    | "depopSales"
    | "depopInventory"
) {
  const plan = userPlanSelector(state);

  if (plan === "ultimate") return true;

  return checkIfItIsInitialImport(state, key);
}

type PoshmarkSyncDatePropertyName =
  | "poshmarkLastSyncDateInventory"
  | "poshmarkLastSyncDateTransactions";
type MercariSyncDatePropertyName =
  | "mercariLastSyncDateInventory"
  | "mercariLastSyncDateTransactions";
type WhatnotSyncDatePropertyName = "whatnotLastSyncDateTransactions";
type VintedSyncDatePropertyName =
  | "vintedLastSyncDateSales"
  | "vintedLastSyncDateInventory"
  | "vintedLastSyncDateTransactions";
type DepopSyncDatePropertyName =
  | "depopLastSyncDateSales"
  | "depopLastSyncDateInventory";

export async function saveSyncDate(
  integrationId: string,
  propertyName:
    | PoshmarkSyncDatePropertyName
    | MercariSyncDatePropertyName
    | WhatnotSyncDatePropertyName
    | VintedSyncDatePropertyName
    | DepopSyncDatePropertyName,
  date: string
) {
  const db = firestore();
  await db
    .collection("Plaid_Integrations")
    .doc(integrationId)
    .update({
      [propertyName]: date,
    });
}

export function parseDate(
  date: string | undefined | null,
  fallback: Date
): Date {
  if (date) {
    const d = new Date(date || "");
    if (!isNaN(d.getTime())) return d;
  }

  return fallback;
}

export function getLastSyncDate(
  integrationData: MercariIntegration,
  propertyName: MercariSyncDatePropertyName
);
export function getLastSyncDate(
  integrationData: PoshmarkIntegration,
  propertyName: PoshmarkSyncDatePropertyName
);
export function getLastSyncDate(
  integrationData: WhatnotIntegration,
  propertyName: WhatnotSyncDatePropertyName
);
export function getLastSyncDate(
  integrationData: VintedIntegration,
  propertyName: VintedSyncDatePropertyName
);
export function getLastSyncDate(
  integrationData: DepopIntegration,
  propertyName: DepopSyncDatePropertyName
);
export function getLastSyncDate(integrationData, propertyName) {
  const lastDate = integrationData[propertyName];
  if (lastDate?.match(/\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/)) return lastDate;
  const date = new Date();
  const match = lastDate?.match(/(\d{4})-(\d{2})-(\d{2})/);
  if (match) {
    date.setFullYear(parseInt(match[1], 10));
    date.setMonth(parseInt(match[2], 10) - 1);
    date.setDate(parseInt(match[3], 10));
  } else {
    date.setDate(date.getDate() - 1);
  }
  date.setHours(0, 0, 0, 0);
  return date.toISOString();
}

export async function shouldRunDailySync(
  integrationId: string,
  reSyncInterval: number
) {
  const db = firestore();

  return await db.runTransaction(async (t) => {
    const doc = await t.get(
      db.collection("Plaid_Integrations").doc(integrationId)
    );
    const data = doc.data();
    const lastSync = parseDate(
      data?.lastSync,
      new Date("2000-01-01T01:00:00.000Z")
    );
    return Date.now() - lastSync.getTime() >= reSyncInterval;
  });
}

export type IntegrationPlatformName =
  | "Poshmark"
  | "Mercari"
  | "Whatnot"
  | "Vinted"
  | "Depop";

const beforeUnloadPlatforms: IntegrationPlatformName[] = [];

function formatPlatformList(platforms: IntegrationPlatformName[]): string {
  if (platforms.length === 1) return platforms[0];
  if (platforms.length === 2) return `${platforms[0]} and ${platforms[1]}`;
  return `${platforms.slice(0, -1).join(", ")} and ${
    platforms[platforms.length - 1]
  }`;
}

function getBeforeUnloadMessage(): string {
  return `${formatPlatformList(
    beforeUnloadPlatforms
  )} sync is currently running, are you sure to leave My Reseller Genie?`;
}

function ensureBeforeUnloadListener() {
  if ((window as any)._beforeonloadhandler) return;

  const beforeunloadhandler = ((window as any)._beforeonloadhandler = (
    e: BeforeUnloadEvent
  ) => {
    if (beforeUnloadPlatforms.length === 0) {
      window.removeEventListener("beforeunload", beforeunloadhandler);
      delete (window as any)._beforeonloadhandler;
      return;
    }
    e.preventDefault();
    const text = getBeforeUnloadMessage();
    e.returnValue = text;
    return text;
  });
  window.addEventListener("beforeunload", beforeunloadhandler);
}

export function setBeforeUnloadHandler(platform: IntegrationPlatformName) {
  if (beforeUnloadPlatforms.includes(platform)) return;
  beforeUnloadPlatforms.push(platform);
  ensureBeforeUnloadListener();
}

export function clearBeforeUnloadHandler(platform: IntegrationPlatformName) {
  const index = beforeUnloadPlatforms.indexOf(platform);
  if (index !== -1) {
    beforeUnloadPlatforms.splice(index, 1);
  }
  if (
    beforeUnloadPlatforms.length === 0 &&
    (window as any)._beforeonloadhandler
  ) {
    window.removeEventListener(
      "beforeunload",
      (window as any)._beforeonloadhandler
    );
    delete (window as any)._beforeonloadhandler;
  }
}

function round(number, fixed = 2) {
  const e = Math.pow(10, fixed);
  return Math.round(number * e) / e;
}

export class Allocator {
  value: number;
  divider: number;
  length: number;
  fixed: number;
  allocated: number;
  currentIndex = 0;

  /**
   * @param {number} value - total "discount"
   * @param {number} divider - eg.: total price
   * @param {number} length - amount of items
   * @param {number} fixed - decimal rounding
   */
  constructor(value: number, divider: number, length: number, fixed: number) {
    this.value = value;
    this.divider = divider;
    this.length = length;
    this.fixed = fixed;
    this.allocated = 0;
    this.currentIndex = 0;
  }

  /**
   * @param {number} dividend - eg.: price
   * @param {number} index - index of element
   * @return {number} allocated value
   */
  getAllocation(dividend) {
    const index = this.currentIndex;
    this.currentIndex += 1;
    if (!this.divider) return this.value;

    const last = index === this.length - 1;
    if (last) return this.value - this.allocated;

    let allocated = (this.value * dividend) / this.divider;

    if (this.fixed) allocated = round(allocated, this.fixed);

    this.allocated += allocated;
    return allocated;
  }
}

/**
 * Check if the browser extension is installed (and optionally, if it's up to date)
 * and dispatch error status if not.
 * @param requiredVersion - Optional minimum version required. If provided, calls checkExtensionVersion().
 * @returns extensionId if successful, null if extension check failed (error already dispatched)
 */
export function checkExtensionOrDispatchError(
  dispatch: Function,
  updateSyncStatus: (status: SyncStatus | undefined) => any,
  platform: IntegrationPlatformName,
  dispatcher?: Dispatcher,
  requiredVersion?: string
): string | null {
  try {
    const extensionId = getExtensionId();
    if (requiredVersion) {
      checkExtensionVersion(requiredVersion);
    }
    return extensionId;
  } catch (e) {
    if (e instanceof MissingExtensionError) {
      dispatch(
        updateSyncStatus({
          type: "error",
          message: `Looks like you don't have the My Reseller Genie extension installed. Go to the "Integrations" tab and click the "Pull Data Now" button for "${platform}". Follow the prompt to download the extension.`,
          dispatcher,
          code: "install-extension",
        })
      );
    } else if (e instanceof OutdatedExtensionError) {
      dispatch(
        updateSyncStatus({
          type: "error",
          message: `Looks like you have an outdated My Reseller Genie extension installed. Go to the "Integrations" tab and click the "Pull Data Now" button for "${platform}". Follow the prompt to download the new extension.`,
          dispatcher,
          code: "install-extension",
        })
      );
    } else {
      dispatch(
        updateSyncStatus({
          type: "error",
          message: (e as Error).message || (e as Error).toString(),
          dispatcher,
          code: "unknown",
        })
      );
    }
    return null;
  }
}
